import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { yearForPeriod, fmt } from "./reportUtils";

interface AnnualAccountsReportProps {
  period: string;
}

const AnnualAccountsReport: React.FC<AnnualAccountsReportProps> = ({
  period,
}) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const year = yearForPeriod(period);
        const data = await reportsApi.getAnnualAccounts(
          currentBuilding.id,
          year,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching annual accounts:", err);
        if (!cancelled)
          setError("Failed to load annual accounts. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading annual accounts..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No annual accounts data available" />;
  }

  const { profitLoss, balanceSheet, serviceCharge, reserveFund } = report;

  return (
    <div className="print:text-xs">
      {/* Title Page */}
      <div className="bg-white shadow rounded-lg p-8 mb-8 print:break-after-page">
        <div className="text-center mb-8 print:mb-4">
          <h1 className="text-3xl font-bold text-gray-900 print:text-2xl">
            Annual Accounts
          </h1>
          <p className="text-xl text-gray-600 mt-2 print:text-lg">
            Year Ended 31 December {report.year}
          </p>
          <p className="text-lg text-gray-500 mt-4 print:text-base">
            {currentBuilding?.name}
          </p>
        </div>

        <div className="border-t border-gray-300 pt-8 mt-8 print:pt-4 print:mt-4">
          <p className="text-sm text-gray-600">Prepared by: Ledgiventa</p>
          <p className="text-sm text-gray-600">
            Date: {new Date().toLocaleDateString("en-GB")}
          </p>
        </div>
      </div>

      {/* Profit & Loss Summary */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
        <div className="bg-primary-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 print:text-xl">
            Statement of Comprehensive Income
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            For the year ended 31 December {report.year}
          </p>
        </div>

        <div className="px-6 py-6">
          <div className="space-y-4">
            <div className="flex justify-between border-b pb-4">
              <span className="font-medium text-gray-600">Total Income</span>
              <span className="font-semibold text-gray-900">
                {fmt(profitLoss?.totalIncome || 0)}
              </span>
            </div>
            <div className="flex justify-between border-b pb-4">
              <span className="font-medium text-gray-600">Total Bills</span>
              <span className="font-semibold text-gray-900">
                {fmt(profitLoss?.totalBills || 0)}
              </span>
            </div>
            <div className="flex justify-between bg-primary-50 px-4 py-3 rounded">
              <span className="font-semibold text-gray-900">
                Net Income / (Loss)
              </span>
              <span
                className={`font-bold text-lg ${
                  (profitLoss?.netIncome || 0) >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {fmt(profitLoss?.netIncome || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Sheet Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 print:gap-4">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-green-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Assets</h3>
          </div>
          <div className="px-6 py-6">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Cash at Bank</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.assets?.cashAtBank || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Reserve Funds</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.assets?.reserveFunds || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Service Charge Debtors</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.assets?.serviceChargeDebtors || 0)}
                </span>
              </div>
              <div className="border-t pt-3 flex justify-between font-semibold">
                <span className="text-gray-900">Total Assets</span>
                <span className="text-primary-600">
                  {fmt(balanceSheet?.assets?.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">
              Liabilities & Equity
            </h3>
          </div>
          <div className="px-6 py-6">
            <div className="space-y-3">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Liabilities
              </div>
              <div className="flex justify-between text-sm ml-2">
                <span className="text-gray-600">SC Pool (held on trust)</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.liabilities?.serviceChargePool || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm ml-2">
                <span className="text-gray-600">Unpaid Invoices</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.liabilities?.unpaidInvoices || 0)}
                </span>
              </div>
              <div className="border-t pt-2 ml-2 mb-2">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-gray-900">Total Liabilities</span>
                  <span className="text-red-600">
                    {fmt(balanceSheet?.liabilities?.total || 0)}
                  </span>
                </div>
              </div>

              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Equity
              </div>
              <div className="flex justify-between text-sm ml-2 mb-2">
                <span className="text-gray-600">Retained Earnings</span>
                <span className="font-medium text-gray-900">
                  {fmt(balanceSheet?.equity?.retained || 0)}
                </span>
              </div>

              <div className="border-t pt-3 flex justify-between font-semibold">
                <span className="text-gray-900">Total Liab. & Equity</span>
                <span className="text-primary-600">
                  {fmt(balanceSheet?.totalLiabilitiesAndEquity || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Charge Collection Summary */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
        <div className="bg-purple-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">
            Service Charge Collection
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Year ended 31 December {report.year}
          </p>
        </div>
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Total Charged
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {fmt(serviceCharge?.totalCharged || 0)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Total Collected
              </p>
              <p className="text-2xl font-bold text-green-600">
                {fmt(serviceCharge?.totalCollected || 0)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Collection Rate
              </p>
              <p className="text-2xl font-bold text-primary-600">
                {(serviceCharge?.collectionRate || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Reserve Fund Summary */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-orange-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">
            Reserve Fund Summary
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            At 31 December {report.year}
          </p>
        </div>
        <div className="px-6 py-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Opening Balance</span>
              <span className="font-semibold text-gray-900">
                {fmt(reserveFund?.totalOpeningBalance || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Contributions</span>
              <span className="font-semibold text-green-600">
                {fmt(reserveFund?.totalContributions || 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Withdrawals</span>
              <span className="font-semibold text-red-600">
                {fmt(reserveFund?.totalWithdrawals || 0)}
              </span>
            </div>
            <div className="border-t pt-3 flex justify-between bg-orange-50 px-4 py-3 rounded">
              <span className="font-semibold text-gray-900">
                Closing Balance
              </span>
              <span className="font-bold text-lg text-orange-600">
                {fmt(reserveFund?.totalClosingBalance || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-8 border-t border-gray-300 text-center text-xs text-gray-500 print:mt-8 print:pt-4">
        <p>End of Annual Accounts</p>
        <p className="mt-2">
          Generated by Ledgiventa on {new Date().toLocaleDateString("en-GB")}
        </p>
      </div>
    </div>
  );
};

export default AnnualAccountsReport;
